import { ZodError } from 'zod';
import { formatDay, formatDayKey } from '../../lib/format.js';
import type { UsagePeriod } from './useUsageLogic.js';

/** How the page groups the API's sources, in stacking order, each with its chart color. */
export const SOURCE_GROUPS = [
  { name: 'Chat', color: 'var(--chart-1)', sources: ['chat'] },
  { name: 'History summaries', color: 'var(--chart-2)', sources: ['history_summary'] },
  { name: 'Chat titles', color: 'var(--chart-3)', sources: ['conversation_title'] },
  {
    name: 'Workflows',
    color: 'var(--chart-4)',
    sources: ['workflow_agent_step', 'bot_delivery'],
  },
  {
    name: 'Reports',
    color: 'var(--chart-5)',
    sources: ['report_narrative', 'report_running_judge'],
  },
  {
    name: 'Feedback',
    color: 'var(--chart-6)',
    sources: ['feedback_triage', 'feedback_escalation'],
  },
  {
    name: 'Admin helpers',
    color: 'var(--chart-7)',
    sources: ['builder_helper', 'policy_format', 'knowledge_authoring'],
  },
] as const;

export type SourceGroupName = (typeof SOURCE_GROUPS)[number]['name'] | 'Other';

/** Each source by name, finer than the chart groups: letters and workflow steps apart. */
const SOURCE_NAMES: Record<string, string> = {
  chat: 'Chat',
  bot_delivery: 'Bot letters',
  watch_delivery: 'Watch deliveries',
  workflow_agent_step: 'Workflow steps',
  history_summary: 'History summaries',
  conversation_title: 'Chat titles',
  report_narrative: 'Report narratives',
  report_running_judge: 'Report running check',
  feedback_triage: 'Feedback triage',
  feedback_escalation: 'Feedback escalation',
  builder_helper: 'Workflow builder helper',
  policy_format: 'Policy formatting',
  knowledge_authoring: 'Knowledge authoring',
};

/** A source's display name; one the page doesn't know yet reads from its id. */
export const sourceName = (source: string): string =>
  SOURCE_NAMES[source] ?? source.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());

const UNIT_NOUNS = {
  turn: ['turn', 'turns'],
  letter: ['letter', 'letters'],
  delivery: ['delivery', 'deliveries'],
  step: ['step', 'steps'],
  call: ['call', 'calls'],
} as const;

/** The unit a source counts, singular or plural. */
export const unitNoun = (unit: keyof typeof UNIT_NOUNS | undefined, count: number): string =>
  UNIT_NOUNS[unit ?? 'call'][count === 1 ? 0 : 1];

/** The group a source belongs to; a source the page doesn't know yet is "Other". */
export const sourceGroupOf = (source: string): SourceGroupName =>
  SOURCE_GROUPS.find((g) => (g.sources as readonly string[]).includes(source))?.name ?? 'Other';

const MODEL_NAMES: Record<string, string> = {
  'claude-opus-5-5': 'Claude Opus 5.5',
  'claude-opus-5': 'Claude Opus 5',
  'claude-opus-4-8': 'Claude Opus 4.8',
  'claude-sonnet-5': 'Claude Sonnet 5',
  'claude-sonnet-4-6': 'Claude Sonnet 4.6',
  'claude-haiku-4-5-20251001': 'Claude Haiku 4.5',
};

/** A model id as people say it; an unknown id shows as it is. */
export const modelName = (model: string): string => MODEL_NAMES[model] ?? model;

/** Changes worth marking on the cost-per-answer line, by tenant day. */
export const COST_MARKERS = [{ date: '2026-10-07', label: 'History budget and Opus 5.5' }];

/** The period as a subtitle tail and tile caption: "last 30 days", or "Sep 1 to Sep 30". */
export const periodCaption = (p: UsagePeriod): string => {
  if (p.kind === 'custom') return `${formatDayKey(p.from)} to ${formatDayKey(p.to)}`;
  if (p.period === '24h') return 'last 24 hours';
  return `last ${p.period.slice(0, -1)} days`;
};

/** The same caption, capitalized, for a card's corner: "Last 30 days". */
export const periodHeading = (p: UsagePeriod): string => {
  const caption = periodCaption(p);
  return caption.charAt(0).toUpperCase() + caption.slice(1);
};

/** What "before" means for the period's comparison: "a month ago", "the day before". */
export const previousPhrase = (p: UsagePeriod): string => {
  if (p.kind === 'custom') return 'the period before';
  switch (p.period) {
    case '24h':
      return 'the day before';
    case '7d':
      return 'a week ago';
    case '30d':
      return 'a month ago';
    default:
      return `the ${p.period.slice(0, -1)} days before`;
  }
};

/** The range the API resolved, for the subtitle: "Sep 8 to Oct 7". */
export const resolvedRange = (since: string, until: string): string =>
  `${formatDay(since)} to ${formatDay(until)}`;

/** A load error in words: a response in an unexpected shape means the API is a version apart. */
export const usageErrorMessage = (error: Error): string =>
  error instanceof ZodError
    ? 'The usage service answered in a shape this page doesn’t expect. It may be mid-update; try again in a few minutes.'
    : error.message;
